// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref } from 'firebase/storage';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { doc, getDoc } from 'firebase/firestore';

import { fetchBreweryClaimsForBrewery } from '../../services/breweryClaims';
import { fetchBreweryPermissionsForBrewery } from '../../services/breweryPermissions';
import { mapPermissionsBySourceClaimId } from '../../utils/breweryClaimPermissionLinks';
import { db, functions, storage } from '../../utils/firebase';

const REMOVE_BREWERY_ADMIN_CALLABLE =
  'callableBreweryRemoveBreweryAdmin-removeBreweryAdmin';

function formatFirestoreTimestamp(value) {
  if (!value || typeof value.toDate !== 'function') {
    return '—';
  }
  try {
    return value.toDate().toLocaleString();
  } catch {
    return '—';
  }
}

function formatClaimantFromClaim(row) {
  const name = typeof row.requesterName === 'string' ? row.requesterName.trim() : '';
  const email = typeof row.requesterEmail === 'string' ? row.requesterEmail.trim() : '';
  const uid = typeof row.userId === 'string' ? row.userId.trim() : '';
  const parts = [];
  if (name) {
    parts.push(name);
  }
  if (email) {
    parts.push(email);
  }
  if (!name && !email && uid) {
    parts.push(uid);
  }
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function normalizedClaimStatus(row) {
  return typeof row.status === 'string' ? row.status : 'pending';
}

/**
 * @param {Record<string, unknown>} userData
 * @param {string} userId
 */
function displayNameFromUserDoc(userData, userId) {
  const firstName = typeof userData.firstName === 'string' ? userData.firstName.trim() : '';
  const lastName = typeof userData.lastName === 'string' ? userData.lastName.trim() : '';
  const displayName =
    typeof userData.displayName === 'string' ? userData.displayName.trim() : '';
  const email = typeof userData.email === 'string' ? userData.email.trim() : '';
  const fromFirstLast = `${firstName} ${lastName}`.trim();
  if (displayName) {
    return displayName;
  }
  if (fromFirstLast) {
    return fromFirstLast;
  }
  if (email) {
    return email;
  }
  return userId || '—';
}

/**
 * @param {Record<string, unknown>} userData
 */
function emailFromUserDoc(userData) {
  const email = typeof userData.email === 'string' ? userData.email.trim() : '';
  return email;
}

async function loadUserProfilesForPermissionRows(permissionRows) {
  const userIds = [
    ...new Set(
      permissionRows
        .map((row) => (typeof row.userId === 'string' ? row.userId.trim() : ''))
        .filter((uid) => uid.length > 0),
    ),
  ];
  const entries = await Promise.all(
    userIds.map(async (userId) => {
      const userSnap = await getDoc(doc(db, 'users', userId));
      const data = userSnap.exists() ? userSnap.data() : {};
      const displayName = displayNameFromUserDoc(data, userId);
      const email = emailFromUserDoc(data);
      return [
        userId,
        {
          displayName,
          email,
        },
      ];
    }),
  );
  return Object.fromEntries(entries);
}

function ClaimsTableSection({ claims, permissionByClaimId, proofUrlByClaimId }) {
  return (
    <>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Claims
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Claimee</TableCell>
              <TableCell>Claim date</TableCell>
              <TableCell>Approved date</TableCell>
              <TableCell>Proof</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {claims.map((claimRow) => {
              const perm = permissionByClaimId.get(claimRow.id);
              const approvedAt = perm ? formatFirestoreTimestamp(perm.createdAt) : '—';
              const proofUrl = proofUrlByClaimId[claimRow.id] ?? null;
              return (
                <TableRow key={claimRow.id}>
                  <TableCell>{normalizedClaimStatus(claimRow)}</TableCell>
                  <TableCell>{formatClaimantFromClaim(claimRow)}</TableCell>
                  <TableCell>{formatFirestoreTimestamp(claimRow.createdAt)}</TableCell>
                  <TableCell>{approvedAt}</TableCell>
                  <TableCell>
                    {proofUrl ? (
                      <a href={proofUrl} target="_blank" rel="noopener noreferrer">
                        View document
                      </a>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}


function PermissionRemoveButton({ permissionRow, onRequestRemove }) {
  const handleClick = useCallback(() => {
    onRequestRemove(permissionRow);
  }, [onRequestRemove, permissionRow]);
  return (
    <Button size="small" color="error" onClick={handleClick}>
      Remove
    </Button>
  );
}

function BreweryPermissionsTableSection({
  permissions,
  userProfilesById,
  onRequestRemoveUser,
}) {
  return (
    <>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Users
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Added</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.map((permissionRow) => {
              const uid =
                typeof permissionRow.userId === 'string' ? permissionRow.userId.trim() : '';
              const profile = userProfilesById[uid] ?? {};
              const displayName =
                typeof profile.displayName === 'string' && profile.displayName.trim().length > 0
                  ? profile.displayName
                  : uid || '—';
              const role = typeof permissionRow.role === 'string' ? permissionRow.role : '—';
              return (
                <TableRow key={permissionRow.id}>
                  <TableCell>{displayName}</TableCell>
                  <TableCell>{role}</TableCell>
                  <TableCell>{formatFirestoreTimestamp(permissionRow.createdAt)}</TableCell>
                  <TableCell align="right">
                    <PermissionRemoveButton
                      permissionRow={permissionRow}
                      onRequestRemove={onRequestRemoveUser}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function RemoveBreweryAdminDialog({
  open,
  removeStep,
  removeTarget,
  confirmEmailInput,
  confirmEmailInputRef,
  onClose,
  onConfirmEmailChange,
  onContinueStep1,
  onFinalizeRemove,
  removeBusy,
  emailMatches,
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Remove brewery access</DialogTitle>
      <DialogContent>
        {removeStep === 1 ? (
          <DialogContentText>
            Remove portal access for <strong>{removeTarget?.displayName}</strong> (
            {removeTarget?.email})? This permanently deletes their permission for this brewery and
            revokes brewery-admin status if they have no other breweries.
          </DialogContentText>
        ) : (
          <Box sx={{ pt: 1 }}>
            <DialogContentText sx={{ mb: 2 }}>
              Type the user&apos;s email exactly to confirm:{' '}
              <strong>{removeTarget?.email}</strong>
            </DialogContentText>
            <TextField
              inputRef={confirmEmailInputRef}
              fullWidth
              label="User email"
              value={confirmEmailInput}
              onChange={onConfirmEmailChange}
              margin="normal"
              variant="outlined"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={removeBusy}>
          Cancel
        </Button>
        {removeStep === 1 ? (
          <Button onClick={onContinueStep1} color="error" variant="contained">
            Continue
          </Button>
        ) : (
          <Button
            onClick={onFinalizeRemove}
            color="error"
            variant="contained"
            disabled={!emailMatches || removeBusy}
          >
            {removeBusy ? 'Removing…' : 'Remove access'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function BreweryActionErrorSnackbar({ message, onDismiss }) {
  return (
    <Snackbar
      open={Boolean(message)}
      autoHideDuration={8000}
      onClose={onDismiss}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity="error" onClose={onDismiss} sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  );
}

/** Loaded layout split into sections for shallower JSX in the exported component (DeepSource JS-0415). */
function LoadedBreweryClaimUsersPanels({
  claims,
  permissionByClaimId,
  proofUrlByClaimId,
  permissions,
  userProfilesById,
  onRequestRemoveUser,
  removeDialogOpen,
  removeStep,
  removeTarget,
  confirmEmailInput,
  confirmEmailInputRef,
  closeRemoveDialog,
  onConfirmEmailChange,
  handleConfirmRemoveStep1,
  handleFinalRemove,
  removeBusy,
  emailMatches,
  actionError,
  onDismissActionError,
}) {
  return (
    <Box sx={{ p: 2 }}>
      <ClaimsTableSection
        claims={claims}
        permissionByClaimId={permissionByClaimId}
        proofUrlByClaimId={proofUrlByClaimId}
      />
      <BreweryPermissionsTableSection
        permissions={permissions}
        userProfilesById={userProfilesById}
        onRequestRemoveUser={onRequestRemoveUser}
      />
      <RemoveBreweryAdminDialog
        open={removeDialogOpen}
        removeStep={removeStep}
        removeTarget={removeTarget}
        confirmEmailInput={confirmEmailInput}
        confirmEmailInputRef={confirmEmailInputRef}
        onClose={closeRemoveDialog}
        onConfirmEmailChange={onConfirmEmailChange}
        onContinueStep1={handleConfirmRemoveStep1}
        onFinalizeRemove={handleFinalRemove}
        removeBusy={removeBusy}
        emailMatches={emailMatches}
      />
      <BreweryActionErrorSnackbar message={actionError} onDismiss={onDismissActionError} />
    </Box>
  );
}

function BreweryClaimUsersSection({ breweryId }) {
  const [claims, setClaims] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [userProfilesById, setUserProfilesById] = useState({});
  const [proofUrlByClaimId, setProofUrlByClaimId] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [removeBusy, setRemoveBusy] = useState(false);

  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeStep, setRemoveStep] = useState(1);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [confirmEmailInput, setConfirmEmailInput] = useState('');
  const confirmEmailInputRef = useRef(null);
  /** Monotonic id so superseded async load branches do not overwrite state after breweryId changes. */
  const latestLoadGenerationRef = useRef(0);

  const removeCallable = useMemo(
    () => httpsCallable(functions, REMOVE_BREWERY_ADMIN_CALLABLE),
    [],
  );

  const permissionByClaimId = useMemo(
    () => mapPermissionsBySourceClaimId(permissions),
    [permissions],
  );

  const loadAll = useCallback(async () => {
    latestLoadGenerationRef.current += 1;
    const loadGeneration = latestLoadGenerationRef.current;
    const applyIfCurrentLoad = () => loadGeneration === latestLoadGenerationRef.current;

    if (typeof breweryId !== 'string' || breweryId.trim().length === 0) {
      if (!applyIfCurrentLoad()) {
        return;
      }
      setClaims([]);
      setPermissions([]);
      setProofUrlByClaimId({});
      setUserProfilesById({});
      setLoading(false);
      return;
    }
    if (!applyIfCurrentLoad()) {
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const [fetchedClaims, fetchedPermissions] = await Promise.all([
        fetchBreweryClaimsForBrewery(breweryId),
        fetchBreweryPermissionsForBrewery(breweryId),
      ]);
      if (!applyIfCurrentLoad()) {
        return;
      }
      setClaims(fetchedClaims);
      setPermissions(fetchedPermissions);

      const profiles = await loadUserProfilesForPermissionRows(fetchedPermissions);
      if (!applyIfCurrentLoad()) {
        return;
      }
      setUserProfilesById(profiles);

      const proofEntries = await Promise.all(
        fetchedClaims.map(async (claimRow) => {
          const path = claimRow.proofStoragePath;
          if (typeof path !== 'string' || path.length === 0) {
            return [claimRow.id, null];
          }
          try {
            const url = await getDownloadURL(ref(storage, path));
            return [claimRow.id, url];
          } catch {
            return [claimRow.id, null];
          }
        }),
      );
      if (!applyIfCurrentLoad()) {
        return;
      }
      const nextProofMap = {};
      for (const [claimId, url] of proofEntries) {
        nextProofMap[claimId] = url;
      }
      setProofUrlByClaimId(nextProofMap);
    } catch (error) {
      if (!applyIfCurrentLoad()) {
        return;
      }
      const message =
        typeof error?.message === 'string' ? error.message : 'Failed to load claims and users.';
      setLoadError(message);
      setClaims([]);
      setPermissions([]);
    } finally {
      if (applyIfCurrentLoad()) {
        setLoading(false);
      }
    }
  }, [breweryId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const closeRemoveDialog = useCallback(() => {
    setRemoveDialogOpen(false);
    setRemoveStep(1);
    setRemoveTarget(null);
    setConfirmEmailInput('');
    setActionError('');
  }, []);

  const handleClickRemoveUser = useCallback((permissionRow) => {
    const targetUserId =
      typeof permissionRow.userId === 'string' ? permissionRow.userId.trim() : '';
    if (targetUserId.length === 0) {
      return;
    }
    const profile = userProfilesById[targetUserId] ?? {};
    const email = typeof profile.email === 'string' ? profile.email.trim() : '';
    const displayName =
      typeof profile.displayName === 'string' ? profile.displayName.trim() : targetUserId;
    if (!email) {
      setActionError(
        'This user has no email on file. Remove access is only available when an email is stored on their profile.',
      );
      return;
    }
    setActionError('');
    setRemoveTarget({
      userId: targetUserId,
      displayName,
      email,
    });
    setRemoveStep(1);
    setConfirmEmailInput('');
    setRemoveDialogOpen(true);
  }, [userProfilesById]);

  const handleConfirmRemoveStep1 = useCallback(() => {
    setRemoveStep(2);
    setConfirmEmailInput('');
  }, []);

  const handleFinalRemove = useCallback(async () => {
    if (!removeTarget) {
      return;
    }
    setRemoveBusy(true);
    setActionError('');
    try {
      await removeCallable({
        breweryId: breweryId.trim(),
        targetUserId: removeTarget.userId,
      });
      closeRemoveDialog();
      await loadAll();
    } catch (error) {
      const code = typeof error?.code === 'string' ? error.code : '';
      const message = typeof error?.message === 'string' ? error.message : 'Remove failed.';
      setActionError(code ? `${code}: ${message}` : message);
    } finally {
      setRemoveBusy(false);
    }
  }, [breweryId, closeRemoveDialog, loadAll, removeCallable, removeTarget]);

  const emailMatches =
    removeTarget &&
    confirmEmailInput.trim().length > 0 &&
    confirmEmailInput.trim() === removeTarget.email;

  const dismissActionError = useCallback(() => {
    setActionError('');
  }, []);

  const handleConfirmEmailInputChange = useCallback((event) => {
    setConfirmEmailInput(event.target.value);
  }, []);

  useEffect(() => {
    if (!removeDialogOpen || removeStep !== 2) {
      return undefined;
    }
    const timerId = window.setTimeout(() => {
      const inputEl = confirmEmailInputRef.current;
      if (inputEl && typeof inputEl.focus === 'function') {
        inputEl.focus();
      }
    }, 0);
    return () => {
      window.clearTimeout(timerId);
    };
  }, [removeDialogOpen, removeStep]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {loadError}
      </Alert>
    );
  }

  return (
    <LoadedBreweryClaimUsersPanels
      claims={claims}
      permissionByClaimId={permissionByClaimId}
      proofUrlByClaimId={proofUrlByClaimId}
      permissions={permissions}
      userProfilesById={userProfilesById}
      onRequestRemoveUser={handleClickRemoveUser}
      removeDialogOpen={removeDialogOpen}
      removeStep={removeStep}
      removeTarget={removeTarget}
      confirmEmailInput={confirmEmailInput}
      confirmEmailInputRef={confirmEmailInputRef}
      closeRemoveDialog={closeRemoveDialog}
      onConfirmEmailChange={handleConfirmEmailInputChange}
      handleConfirmRemoveStep1={handleConfirmRemoveStep1}
      handleFinalRemove={handleFinalRemove}
      removeBusy={removeBusy}
      emailMatches={emailMatches}
      actionError={actionError}
      onDismissActionError={dismissActionError}
    />
  );
}

BreweryClaimUsersSection.propTypes = {
  breweryId: PropTypes.string.isRequired,
};

export default BreweryClaimUsersSection;
